import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { PieChartMap } from 'Deer/pages/Company/DisciplinaryActions/components/PieChartMap';
import SplitProgressGraph from 'Deer/pages/Company/DisciplinaryActions/components/SplitProgressGraph';
import {
  DISCIPLINARY_ACTIONS_STATUS_COLOR,
  DISCIPLINARY_ACTIONS_STATUS_LABELS,
} from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import { format, parseISO } from 'date-fns';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import QuestionType from 'screens/admin/Reviews/ReviewQuestionBankV1/components/QuestionType';
import { agGridDateComparator } from 'utils/helpers/dateHelpers';
import { checkDemoPredictiveScoreEnabled } from 'utils/helpers/featureFlags';

import { GENDER_MAPPING, QUESTION_TYPES } from 'constants/ProjectConstants';

import { Avatar } from 'uiToolkitV1/Avatar';
import ColoredGridLayout from 'uiToolkitV1/ColoredGridLayout';
import { DateComp } from 'uiToolkitV1/DateComp';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import { GRID_COLOR_MAPPING } from '../ReviewManage/components/ReviewManageAGGrid/ReviewManageAGGridV1';
import PromoStatusV1 from '../ReviewManage/components/ReviewManageAGGrid/components/PromoStatusV1';
import {
  calcReviewStatusFromCount,
  ReviewStatusV1,
} from '../ReviewManage/components/ReviewManageAGGrid/components/ReviewStatusV1';
import { EmpGroupAvatars } from '../ReviewManage/components/ReviewManageAGGrid/components/cellComponents/EmpGroupAvatars';
import {
  REVIEW_STATUS_COLORS,
  REVIEW_STATUS_LABELS,
} from '../ReviewManage/components/ReviewManageTable/components/ShareResultsDrawer/PartialPublishContext';
import { PROMO_STATUS_LABELS } from '../components/SidepanelOverview/SecondaryPanel';
import { STATUS_TO_COLOR_MAP, STATUS_TO_LABEL_MAP } from '../constants/StatusValues';
import { CALIBRATION_STATUS_LABELS } from './columnDefinitionHelpers';
import { CalibrationStatusCellRenderer } from './components/CalibrationStatusCellRenderer';
import { AGGRID_COLUMN_TYPES } from './dataHooks';

const StyledTextBodyMedium = styled(TextBodyMedium)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
`;

const TextBodyMediumWithEmptyValueHandled = ({ children, emptyValueReplacer, customEmptyValue }) => {
  const isEmpty = [null, undefined, ''].includes(children);
  const defaultEmptyValue = customEmptyValue || 'NA';
  const displayValue = isEmpty ? (emptyValueReplacer ?? defaultEmptyValue) : children;

  return (
    <StyledTextBodyMedium color={isEmpty && emptyValueReplacer ? '#999' : '#333'}>{displayValue}</StyledTextBodyMedium>
  );
};

export const CellRenderer = ({ colDef, ...params }) => {
  const {
    columnType,
    colId,
    isCurrentCycle,
    isFirstCalibrationView,
    currentCycleIdNumber = '',
    emptyValueReplacer,
    metadata,
  } = colDef;
  const { isEmployeeSubtitle } = metadata ?? {};

  const { data: rowData, isValueGetter, value } = params;

  switch (columnType) {
    // because this is a group header for grouping purposes only and not a column
    case AGGRID_COLUMN_TYPES.HEADER_GROUP: {
      return {};
    }

    case AGGRID_COLUMN_TYPES.EMPLOYEE: {
      const cellData = rowData?.[colId] ?? {};
      const { displayName, profilePhoto, email } = cellData;

      if (!displayName)
        return (
          <Box className="info">
            <TextBodyMedium>-</TextBodyMedium>
          </Box>
        );

      return (
        <Box pl={1}>
          <ListItemComp
            title={displayName}
            // eslint-disable-next-line react/no-unstable-nested-components
            LeftComponent={() => <Avatar src={profilePhoto} />}
            subTitle={isEmployeeSubtitle ? email : null}
            isBoldText={false}
            withoutBackground
          />
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_RATING: {
      const cellData = rowData?.[colId] ?? {};
      const { ratingText, isCalibrated } = cellData;

      return (
        <Box className="info">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {ratingText}
          </TextBodyMediumWithEmptyValueHandled>
          {isCalibrated && <TextLabelSmall>Calibrated</TextLabelSmall>}
          {!isCalibrated && checkDemoPredictiveScoreEnabled() && isFirstCalibrationView && isCurrentCycle && (
            <TextLabelSmall>Maven suggested</TextLabelSmall>
          )}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.SIMPLE_TEXT: {
      const cellData = isValueGetter ? value : (rowData?.[colId] ?? '');
      return (
        <Tooltip placement="bottom" isDisabled={!cellData} label={cellData || ''}>
          <Box w="100%">
            <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
              {cellData}
            </TextBodyMediumWithEmptyValueHandled>
          </Box>
        </Tooltip>
      );
    }

    case AGGRID_COLUMN_TYPES.COMPANY: {
      const cellData = rowData?.[colId]?.companyName ?? '';

      return (
        <Tooltip placement="bottom" isDisabled={!cellData} label={cellData || ''}>
          <Box w="100%">
            {/* <StyledTextBodyMedium>{cellData || EMPTY_VALUE}</StyledTextBodyMedium> */}
            <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
              {cellData}
            </TextBodyMediumWithEmptyValueHandled>
          </Box>
        </Tooltip>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_RATING:
    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_PROMOTION_STATUS: {
      const cellData = rowData?.[colId] ?? {};
      const { ratingText, isCalibrated, isMavenSuggested } = cellData;

      return (
        <Box className="info">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {ratingText}
          </TextBodyMediumWithEmptyValueHandled>

          {isCalibrated && <TextLabelSmall>Calibrated</TextLabelSmall>}
          {!isCalibrated && isMavenSuggested && <TextLabelSmall>Maven suggested</TextLabelSmall>}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.DECIMAL: {
      const cellData = rowData?.[colId] ?? '';

      return (
        <Box w="100%">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {cellData}
          </TextBodyMediumWithEmptyValueHandled>
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.INTEGER: {
      const cellData = rowData?.[colId] ?? '';

      return (
        <Box w="100%">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {cellData}
          </TextBodyMediumWithEmptyValueHandled>
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.LONG: {
      const cellData = rowData?.[colId] ?? '';

      return (
        <Box w="100%">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {cellData}
          </TextBodyMediumWithEmptyValueHandled>
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.DATE: {
      const value = rowData?.[colId]?.date ?? '';

      return (
        <Box className="info">
          {value ? (
            <DateComp date={value} />
          ) : (
            <TextBodyMediumWithEmptyValueHandled customEmptyValue="-" emptyValueReplacer={emptyValueReplacer}>
              {value}
            </TextBodyMediumWithEmptyValueHandled>
          )}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.DATETIME: {
      const dateString = rowData?.[colId] ?? '';
      if (!dateString) {
        return (
          <TextBodyMediumWithEmptyValueHandled customEmptyValue="-" emptyValueReplacer={emptyValueReplacer}>
            {dateString}
          </TextBodyMediumWithEmptyValueHandled>
        );
      }
      // Parse the ISO string to a Date object
      const date = parseISO(dateString);

      // Format the date to "Apr 8, 2025"
      const formattedDate = format(date, 'MMM d, yyyy');

      // Format the time to "12:00 AM"
      const formattedTime = format(date, 'h:mm a');

      return (
        <Box display="flex" flexDirection="column">
          <TextBodyMedium>{formattedDate}</TextBodyMedium>
          <TextLabel color="#666">{formattedTime}</TextLabel>
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.DISCIPLINARY_ACTION_STATUS: {
      const statusEnum = rowData?.[colId] ?? '';

      return (
        <Box className="info">
          {statusEnum ? (
            <TagColored color={DISCIPLINARY_ACTIONS_STATUS_COLOR[statusEnum]}>
              {DISCIPLINARY_ACTIONS_STATUS_LABELS[statusEnum]}
            </TagColored>
          ) : (
            <TextBodyMedium>NA</TextBodyMedium>
          )}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_OUTCOME: {
      const cellData = rowData?.[colId] ?? {};
      const { promotionStatus, isCalibrated } = cellData;

      if (isCurrentCycle && checkDemoPredictiveScoreEnabled() && isFirstCalibrationView) {
        const overallRatingCellData = rowData?.[`OVERALL_RATING_${currentCycleIdNumber}`] ?? {};
        const { isCalibrated: isOverallCalibrated } = overallRatingCellData;

        return (
          <Box className="info">
            {promotionStatus ? <PromoStatusV1 status={promotionStatus} /> : <TextBodyMedium>NA</TextBodyMedium>}
            {isOverallCalibrated ? (
              <TextLabelSmall>Calibrated</TextLabelSmall>
            ) : (
              <TextLabelSmall>Maven suggested</TextLabelSmall>
            )}
          </Box>
        );
      }

      return (
        <Box className="info">
          {promotionStatus ? <PromoStatusV1 status={promotionStatus} /> : <TextBodyMedium>NA</TextBodyMedium>}
          {isCalibrated && <TextLabelSmall>Calibrated</TextLabelSmall>}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_OUTCOME_NEW: {
      const cellData = rowData?.[colId] ?? {};
      const { ratingText, isCalibrated } = cellData;

      return (
        <Box className="info">
          <TextBodyMediumWithEmptyValueHandled emptyValueReplacer={emptyValueReplacer}>
            {ratingText}
          </TextBodyMediumWithEmptyValueHandled>

          {isCalibrated ? <TextLabelSmall>Calibrated</TextLabelSmall> : null}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_GRID_POSITION:
    case AGGRID_COLUMN_TYPES.REVIEW_GRID_POSITION: {
      const cellData = rowData?.[colId] ?? {};
      // eslint-disable-next-line react/destructuring-assignment
      const { metadata } = colDef ?? {};
      const { colCount, rowCount } = metadata ?? {};

      const { rowNumber, colNumber, color, label, isCalibrated, isMavenSuggested } = cellData;

      const hasData = colCount !== undefined && rowCount !== undefined && label;
      if (isCurrentCycle && checkDemoPredictiveScoreEnabled() && isFirstCalibrationView) {
        const overallRatingCellData = rowData?.[`OVERALL_RATING_${currentCycleIdNumber}`] ?? {};
        const { isCalibrated: isOverallCalibrated } = overallRatingCellData;
        return (
          <Box className="info">
            {hasData && (
              <Flex className="info" justifyContent="center" alignItems="center" gap="8px">
                <Box w={`${Number(colCount) * 8 + (Number(colCount) - 1) * 2}px`}>
                  <ColoredGridLayout
                    rows={Number(rowCount)}
                    name={label}
                    columns={Number(colCount)}
                    selectedRow={Number(rowCount) + 1 - rowNumber || -1}
                    selectedColumn={colNumber || -1}
                    selectedColor={GRID_COLOR_MAPPING[color] || ''}
                  />
                </Box>
                <TextLabel color="text.g2">{label}</TextLabel>
              </Flex>
            )}
            {!hasData && <TextBodyMedium>NA</TextBodyMedium>}

            {isOverallCalibrated ? (
              <TextLabelSmall>Calibrated</TextLabelSmall>
            ) : (
              <TextLabelSmall>Maven suggested</TextLabelSmall>
            )}
          </Box>
        );
      }

      return (
        <Box className="info">
          {hasData && (
            <Flex className="info" justifyContent="center" alignItems="center" gap="8px">
              <Box w={`${Number(colCount) * 8 + (Number(colCount) - 1) * 2}px`}>
                <ColoredGridLayout
                  rows={Number(rowCount)}
                  name={label}
                  columns={Number(colCount)}
                  selectedRow={Number(rowCount) + 1 - rowNumber || -1}
                  selectedColumn={colNumber || -1}
                  selectedColor={GRID_COLOR_MAPPING[color] || ''}
                />
              </Box>
              <TextLabel color="text.g2">{label}</TextLabel>
            </Flex>
          )}
          {!hasData && <TextBodyMedium>NA</TextBodyMedium>}

          {isCalibrated && <TextLabelSmall>Calibrated</TextLabelSmall>}
          {!isCalibrated && isMavenSuggested && <TextLabelSmall>Maven suggested</TextLabelSmall>}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_STATUS: {
      const statusEnum = rowData?.[colId] ?? '';

      return (
        <Box className="info">
          {statusEnum ? (
            <TagColored color={REVIEW_STATUS_COLORS[statusEnum]}>{REVIEW_STATUS_LABELS[statusEnum]}</TagColored>
          ) : (
            <TextBodyMedium>NA</TextBodyMedium>
          )}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.EMPLOYEE_LIST: {
      const employees = rowData?.[colId]?.employeeList ?? [];
      return (
        <EmpGroupAvatars
          employees={employees.map(({ displayName, profilePhoto, id }) => ({
            name: displayName,
            imageUrl: profilePhoto,
            uuid: id,
          }))}
        />
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_SUBMISSION_STATUS: {
      const statusValue = rowData?.[colId] ?? '';
      return (
        <Box className="info">
          {statusValue && STATUS_TO_LABEL_MAP[statusValue] ? (
            <TagColored color={STATUS_TO_COLOR_MAP[statusValue]}>{STATUS_TO_LABEL_MAP[statusValue]}</TagColored>
          ) : (
            <TextBodyMedium>NA</TextBodyMedium>
          )}
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_COMPLETION_STATUS: {
      const cellData = rowData?.[colId]
        ? { value: { submitted: rowData?.[colId]?.completed, total: rowData?.[colId]?.total } }
        : { value: { submitted: 0, total: 0 } };

      return (
        <Box className="info">
          <ReviewStatusV1 cellData={cellData} />
        </Box>
      );
    }

    case AGGRID_COLUMN_TYPES.REVIEW_CALIBRATION_STATUS: {
      const calibrationStatus = rowData?.[colId] ?? null;
      const selectedEmployeeId = rowData?.EMPLOYEE?.id ?? null;

      return <CalibrationStatusCellRenderer status={calibrationStatus} employeeId={selectedEmployeeId} />;
    }

    case AGGRID_COLUMN_TYPES.SPLIT_PROGRESS_MAP: {
      const map = rowData?.[colId] ?? null;

      return <SplitProgressGraph graphData={map} />;
    }

    case AGGRID_COLUMN_TYPES.PIE_CHART_MAP: {
      const chartData = rowData?.[colId] ?? [];

      return <PieChartMap chartData={chartData} />;
    }

    default:
      return undefined;
  }
};

export function getColumnDefinitionByType(col) {
  const defaultColProps = {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    headerComponent: CustomHeaderCell,
    autoHeaderHeight: true,
    wrapHeaderText: true,
    // for use in CellRenderer
    columnType: col.columnType,
    metadata: col.metadata,
    parentColumnId: col.parentColumnId,
  };

  switch (col.columnType) {
    // because this is a group header for grouping purposes only and not a column
    case AGGRID_COLUMN_TYPES.HEADER_GROUP: {
      return defaultColProps;
    }

    case AGGRID_COLUMN_TYPES.EMPLOYEE: {
      return {
        ...defaultColProps,
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.displayName,
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_RATING: {
      const { ratingType } = col.metadata ?? {};
      return {
        ...defaultColProps,
        // aggrid expects width in number instead of string now.
        width: 130,

        sortable: true,
        filter: ratingType === 'DROP_DOWN' ? 'agSetColumnFilter' : 'agNumberColumnFilter',
        valueGetter: (params) => {
          const ratingType1 = params?.data?.[params.colDef.id]?.ratingType;

          if (params?.data?.[params.colDef.id]?.ratingText === undefined) return undefined;

          if (ratingType1 === 'NUMBER_RATING') {
            return Number(params?.data?.[params.colDef.id]?.ratingText);
          }

          return params?.data?.[params.colDef.id]?.ratingText;
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.SIMPLE_TEXT: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id],
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_RATING: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.ratingText,
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_PROMOTION_STATUS: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.ratingText,
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.DECIMAL: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params?.data?.[params.colDef.id] === undefined || params?.data?.[params.colDef.id] === null)
            return params?.data?.[params.colDef.id];

          return Number(Number(params?.data?.[params.colDef.id]).toFixed(2)); // 2 digit decimal number
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.INTEGER: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params?.data?.[params.colDef.id] === undefined || params?.data?.[params.colDef.id] === null)
            return params?.data?.[params.colDef.id];

          return Number(Number(params?.data?.[params.colDef.id]));
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.LONG: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          if (params?.data?.[params.colDef.id] === undefined || params?.data?.[params.colDef.id] === null)
            return params?.data?.[params.colDef.id];

          return Number(Number(params?.data?.[params.colDef.id]));
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.DATE: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.date,

        filterParams: {
          comparator: agGridDateComparator,
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.DATETIME: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id],
        minWidth: 150,
        cellStyle: {
          width: '150px',
        },
        headerStyle: {
          width: '150px',
        },
        filterParams: {
          comparator: agGridDateComparator,
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_OUTCOME: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => PROMO_STATUS_LABELS[params?.data?.[params.colDef.id]?.promotionStatus] ?? '',
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_OUTCOME_NEW: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.ratingText ?? '',
        cellRenderer: CellRenderer,
        filterValueGetter: (params) => params?.data?.[params.colDef.id]?.ratingText ?? '',
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_STATUS: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => REVIEW_STATUS_LABELS[params?.data?.[params.colDef.id]] ?? '',
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.DISCIPLINARY_ACTION_STATUS: {
      return {
        ...defaultColProps,
        minWidth: 150,
        cellStyle: {
          width: '150px',
        },
        headerStyle: {
          width: '150px',
        },
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => DISCIPLINARY_ACTIONS_STATUS_LABELS[params?.data?.[params.colDef.id]] ?? '',
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_GRID_POSITION:
    case AGGRID_COLUMN_TYPES.REVIEW_GRID_POSITION: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.label,
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.EMPLOYEE_LIST: {
      return {
        ...defaultColProps,
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => {
          const employeeList = params?.data?.[params.colDef.id]?.employeeList ?? [];

          return employeeList.map(({ displayName }) => displayName).join(', ');
        },
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_SUBMISSION_STATUS: {
      return {
        ...defaultColProps,

        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => STATUS_TO_LABEL_MAP?.[params?.data?.[params.colDef.id]],
        cellRenderer: CellRenderer,
        filterValueGetter: (params) => STATUS_TO_LABEL_MAP[params?.data?.[params.colDef.id]] || '',
        filterParams: {
          values: ['Not Started', 'In Progress', 'Completed'],
        },
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_COMPLETION_STATUS: {
      return {
        ...defaultColProps,

        sortable: true,
        valueGetter: (params) => {
          const hasData = !!params?.data?.[params.colDef.id];
          if (!hasData) return 'NA';
          const submitted = params?.data?.[params.colDef.id]?.completed;
          const total = params?.data?.[params.colDef.id]?.total;

          const { label } = calcReviewStatusFromCount({ submitted, total });
          return `${label}: ${submitted}/${total}`;
        },
        cellRenderer: CellRenderer,
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
          const { label } = calcReviewStatusFromCount(
            {
              submitted: params?.data?.[params.colDef.id]?.completed,
              total: params?.data?.[params.colDef.id]?.total,
            } ?? {}
          );
          return label;
        },
        filterParams: {
          values: ['Not Started', 'In Progress', 'Completed', 'NA'],
        },
      };
    }

    case AGGRID_COLUMN_TYPES.REVIEW_CALIBRATION_STATUS: {
      return {
        ...defaultColProps,
        hide: false,
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => CALIBRATION_STATUS_LABELS[params?.data?.[params.colDef.id]] ?? '',
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.COMPANY: {
      return {
        ...defaultColProps,
        // hide: false,
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data?.[params.colDef.id]?.companyName ?? '',
        cellRenderer: CellRenderer,
      };
    }

    case AGGRID_COLUMN_TYPES.PIE_CHART_MAP: {
      return {
        ...defaultColProps,
        sortable: false,
        valueGetter: (params) => params?.data?.[params.colDef.id] ?? {},
        cellRenderer: CellRenderer,
        flex: 1,
      };
    }

    case AGGRID_COLUMN_TYPES.SPLIT_PROGRESS_MAP: {
      return {
        ...defaultColProps,
        sortable: false,
        valueGetter: (params) => params?.data?.[params.colDef.id] ?? {},
        cellRenderer: CellRenderer,
        flex: 1,
      };
    }

    default:
      return new Error('Invalid column type');
  }
}

export function getColumnDefinitionById(col) {
  const definition = {};

  if (col.columnId === 'QUESTION_TYPE') {
    definition.valueGetter = (params) => params.data?.QUESTION_TYPE;
    definition.valueFormatter = (params) => params.data?.QUESTION_TYPE;
    definition.cellRenderer = QuestionType;
    definition.cellRendererParams = { keyName: 'QUESTION_TYPE' };
    definition.filter = 'agSetColumnFilter';
    definition.filterParams = {
      valueFormatter: (params) =>
        QUESTION_TYPES.review.find((item) => item.value === params.value)?.label || params.value,
    };
  } else if (col.columnId === 'GENDER') {
    definition.valueGetter = (params) => GENDER_MAPPING[params.data?.GENDER] || params.data?.GENDER;
    definition.cellRendererParams = { isValueGetter: true };
  }

  return definition;
}
